import * as React from 'react'
import classNames from 'classnames'
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { useTranslation } from 'translations'
import { Locale } from 'admin/articleService'
import { Combobox, Option } from 'uiComponents/combobox/combobox'
import { StatusChip } from 'uiComponents/statusChip/statusChip'
import { SegmentedControl } from 'uiComponents/segmentedControl/segmentedControl'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { ListingOption, TicketCandidate, Translation } from 'channels/googleTTDService'
import { TicketValidityState } from '../../hooks/useTicketValidity'
import { CategoryChips } from '../categoryChips/categoryChips'
import './ticketsTable.scss'

/** What the table knows about a listed ticket beyond the aion option. */
export interface ListedTicket {
    name: string
    internalId: string
    /** Lowest day price today from backoffice; null when nothing is on sale, undefined until the ticket is saved. */
    price?: number | null
    currency: string
    checkoutDescription: Translation[]
}

interface TicketsTableProps {
    options: ListingOption[]
    tickets: Record<string, ListedTicket>
    validity: Record<string, TicketValidityState>
    candidates: TicketCandidate[]
    candidatesLoading: boolean
    /** The listing's categories; a ticket without its own set follows them. */
    venueCategories: string[]
    languages: Locale[]
    canEdit: boolean
    /** Tickets the last save could not put in the feed, with the backoffice reason. */
    refusals: Record<string, string>
    onAdd: (articleUuid: string) => void
    onRemove: (articleUuid: string) => void
    onSetBase: (articleUuid: string) => void
    onDescriptionChange: (articleUuid: string, text: string, language: string) => void
    onCategoryChange: (articleUuid: string, categories: string[] | null) => void
}

const textFor = (translations: Translation[] | undefined, language: string) =>
    translations?.find((translation) => translation.language === language)?.text ?? ''

const formatPrice = (price: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price)

/** The tickets a listing sends to Google: which one is the base ticket, how long each lasts, what Google reads. */
export function TicketsTable(props: TicketsTableProps) {
    const { t } = useTranslation()
    const [expanded, setExpanded] = React.useState<string | null>(null)
    const [language, setLanguage] = React.useState<string>(props.languages[0]?.code ?? '')
    const listed = new Set(props.options.map((option) => option.articleUUID))
    const sameAsVenue = (option: ListingOption) =>
        [...option.category].sort().join() === [...props.venueCategories].sort().join()

    const candidateOptions: Option[] = props.candidates
        .filter((candidate) => !listed.has(candidate.uuid))
        .map((candidate) => {
            const disabledReason = candidate.archived
                ? t('channels.googleTTD.tickets.refusal.article_archived')
                : !candidate.enabled
                ? t('channels.googleTTD.tickets.refusal.article_disabled')
                : undefined
            return {
                value: candidate.uuid,
                label: candidate.name,
                secondaryLabel: `#${candidate.id}`,
                eyebrow: t(`channels.googleTTD.tickets.businessType.${candidate.businessType ?? 'unknown'}`, {
                    defaultValue: t('channels.googleTTD.tickets.businessType.unknown'),
                }),
                disabledReason,
            }
        })

    const validityCell = (uuid: string) => {
        const state = props.validity[uuid]
        if (!state || state.loading) {
            return <SkeletonBar width="6rem" height="1rem" />
        }
        const validity = state.validity
        switch (validity.state) {
            case 'open':
                return <span className="tickets-muted">{t('channels.googleTTD.tickets.noEndDate')}</span>
            case 'ok':
                return format(validity.validTo, 'd MMM yyyy')
            case 'endsSoon':
                return (
                    <StatusChip
                        tone="warning"
                        label={t('channels.googleTTD.tickets.endsIn', { count: validity.daysLeft })}
                    />
                )
            case 'ended':
                return <StatusChip tone="error" label={t('channels.googleTTD.tickets.ended')} />
            default:
                return null
        }
    }

    const googleTextCell = (option: ListingOption) => {
        const filled = props.languages.filter((locale) => textFor(option.description, locale.code).trim())
        if (filled.length === 0) {
            return <StatusChip tone="neutral" label={t('channels.googleTTD.tickets.checkoutText')} />
        }
        return <StatusChip tone="success" label={filled.map((locale) => locale.code.toUpperCase()).join(' ')} />
    }

    return (
        <div className="tickets-table">
            <div className="tickets-toolbar">
                <h3 className="tickets-title">{t('channels.googleTTD.tickets.title')}</h3>
                {props.canEdit && (
                    <Combobox
                        className="tickets-picker"
                        options={candidateOptions}
                        value={null}
                        searchPlaceholder={t('channels.googleTTD.tickets.addPlaceholder')}
                        loading={props.candidatesLoading}
                        getOptionDisabled={(option: Option) => !!option.disabledReason}
                        onChange={(value) => {
                            if (typeof value === 'string') props.onAdd(value)
                        }}
                    />
                )}
            </div>
            {props.options.length === 0 ? (
                <p className="tickets-empty">{t('channels.googleTTD.tickets.empty')}</p>
            ) : (
                <table className="tickets-grid">
                    <thead>
                        <tr>
                            <th scope="col">{t('channels.googleTTD.tickets.base')}</th>
                            <th scope="col">{t('channels.googleTTD.tickets.ticket')}</th>
                            <th scope="col" className="is-number">
                                {t('channels.googleTTD.tickets.priceToday')}
                            </th>
                            <th scope="col">{t('channels.googleTTD.tickets.validUntil')}</th>
                            <th scope="col">{t('channels.googleTTD.tickets.googleText')}</th>
                            <th scope="col">
                                <span className="visually-hidden">{t('channels.googleTTD.tickets.actions')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.options.map((option) => {
                            const uuid = option.articleUUID
                            const ticket = props.tickets[uuid]
                            const refusal = props.refusals[uuid]
                            const open = expanded === uuid
                            return (
                                <React.Fragment key={uuid}>
                                    <tr className={classNames('tickets-row', { open, refused: !!refusal })}>
                                        <td>
                                            <input
                                                type="radio"
                                                className="tickets-radio"
                                                name="gttd-base-ticket"
                                                checked={!!option.baseTicket}
                                                disabled={!props.canEdit}
                                                aria-label={t('channels.googleTTD.tickets.baseFor', {
                                                    ticket: ticket?.name ?? uuid,
                                                })}
                                                onChange={() => props.onSetBase(uuid)}
                                            />
                                        </td>
                                        <td>
                                            <div className="tickets-name">
                                                <span>{ticket?.name ?? t('channels.googleTTD.unknownName')}</span>
                                                <span className="tickets-muted">
                                                    {ticket ? `#${ticket.internalId}` : ''}
                                                    {refusal && (
                                                        <span className="tickets-refusal">
                                                            {' · '}
                                                            {t(`channels.googleTTD.tickets.refusal.${refusal}`, {
                                                                defaultValue: t(
                                                                    'channels.googleTTD.tickets.refusal.refused',
                                                                ),
                                                            })}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="is-number">
                                            {!ticket ? (
                                                <SkeletonBar width="4rem" height="1rem" />
                                            ) : ticket.price === null ? (
                                                <StatusChip
                                                    tone="warning"
                                                    label={t('channels.googleTTD.tickets.noPrice')}
                                                />
                                            ) : ticket.price === undefined ? (
                                                <span className="tickets-muted">
                                                    {t('channels.googleTTD.tickets.priceAfterSave')}
                                                </span>
                                            ) : (
                                                formatPrice(ticket.price, ticket.currency)
                                            )}
                                        </td>
                                        <td>{validityCell(uuid)}</td>
                                        <td>{googleTextCell(option)}</td>
                                        <td className="is-action">
                                            <button
                                                type="button"
                                                className="tickets-expand"
                                                aria-expanded={open}
                                                aria-label={t(
                                                    open
                                                        ? 'channels.googleTTD.tickets.collapse'
                                                        : 'channels.googleTTD.tickets.expand',
                                                    {
                                                        ticket: ticket?.name ?? uuid,
                                                    },
                                                )}
                                                onClick={() => setExpanded(open ? null : uuid)}
                                            >
                                                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr className={classNames('tickets-detail', { open })} aria-hidden={!open}>
                                        <td colSpan={6}>
                                            <div className="tickets-detail-clip">
                                                <div className="tickets-detail-body">
                                                    <div className="tickets-detail-column">
                                                        <div className="tickets-field-head">
                                                            <label
                                                                className="tickets-label"
                                                                htmlFor={`gttd-text-${uuid}-${language}`}
                                                            >
                                                                {t('channels.googleTTD.tickets.googleDescription')}
                                                            </label>
                                                            {props.languages.length > 1 && (
                                                                <SegmentedControl
                                                                    options={props.languages.map((locale) => ({
                                                                        value: locale.code,
                                                                        label: locale.name,
                                                                    }))}
                                                                    value={language}
                                                                    onChange={setLanguage}
                                                                    ariaLabel={t(
                                                                        'channels.googleTTD.venue.translations',
                                                                    )}
                                                                    size="compact"
                                                                />
                                                            )}
                                                        </div>
                                                        <textarea
                                                            id={`gttd-text-${uuid}-${language}`}
                                                            className="tickets-textarea"
                                                            value={textFor(option.description, language)}
                                                            maxLength={2000}
                                                            disabled={!props.canEdit}
                                                            placeholder={t(
                                                                'channels.googleTTD.tickets.googleDescriptionPlaceholder',
                                                            )}
                                                            onChange={(event) =>
                                                                props.onDescriptionChange(
                                                                    uuid,
                                                                    event.target.value,
                                                                    language,
                                                                )
                                                            }
                                                        />
                                                        <p className="tickets-hint">
                                                            {t('channels.googleTTD.tickets.googleDescriptionHint')}
                                                        </p>
                                                    </div>
                                                    <div className="tickets-detail-column">
                                                        <span className="tickets-label">
                                                            {t('channels.googleTTD.tickets.checkoutTextFallback')}
                                                        </span>
                                                        <p className="tickets-checkout-text">
                                                            {textFor(ticket?.checkoutDescription, language) ||
                                                                t('channels.googleTTD.tickets.noCheckoutText')}
                                                        </p>
                                                        <span className="tickets-label" id={`gttd-cats-${uuid}`}>
                                                            {t('channels.googleTTD.tickets.categories')}
                                                        </span>
                                                        <label className="tickets-same-as-venue">
                                                            <input
                                                                type="checkbox"
                                                                checked={sameAsVenue(option)}
                                                                disabled={!props.canEdit}
                                                                onChange={(event) =>
                                                                    props.onCategoryChange(
                                                                        uuid,
                                                                        event.target.checked
                                                                            ? null
                                                                            : [...option.category],
                                                                    )
                                                                }
                                                            />
                                                            {t('channels.googleTTD.tickets.sameAsVenue')}
                                                        </label>
                                                        {!sameAsVenue(option) && (
                                                            <CategoryChips
                                                                selected={option.category}
                                                                onChange={(categories) =>
                                                                    props.onCategoryChange(uuid, categories)
                                                                }
                                                                disabled={!props.canEdit}
                                                                ariaLabelledBy={`gttd-cats-${uuid}`}
                                                            />
                                                        )}
                                                        {props.canEdit && (
                                                            <button
                                                                type="button"
                                                                className="tickets-remove"
                                                                onClick={() => props.onRemove(uuid)}
                                                            >
                                                                <Trash2 size={16} aria-hidden="true" />
                                                                {t('channels.googleTTD.tickets.remove')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            )
                        })}
                    </tbody>
                </table>
            )}
            <p className="tickets-footnote">{t('channels.googleTTD.tickets.footnote')}</p>
        </div>
    )
}
